'use client';

import { ChevronLeft, Eye, Save, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import Cropper from 'react-easy-crop';
import { cn } from '../../utils';
import { Button, Slider } from '../ui';

export const createImage = (url: string): HTMLImageElement =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous'); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  }) as any;

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 */
export async function getCroppedImg(
  imageSrc: any,
  pixelCrop: any,
  fileType: string,
  flip = { horizontal: false, vertical: false }
): Promise<string | null> {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  if (!ctx || !image) {
    return null;
  }

  // set canvas size to match the bounding box
  canvas.width = image.width;
  canvas.height = image.height;

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(image.width / 2, image.height / 2);
  ctx.scale(flip.horizontal ? -1 : 1, flip.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw rotated image
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement('canvas');

  const croppedCtx = croppedCanvas.getContext('2d');

  if (!croppedCtx) {
    return null;
  }

  // Set the size of the cropped canvas
  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  // Draw the cropped image onto the new canvas
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height
  );

  // As Base64 string
  return croppedCanvas.toDataURL(fileType);
}

const DEFAULT_ASPECT_RATIOS = [
  { width: 16, height: 9 },
  { width: 1, height: 1 },
  { width: 3, height: 2 },
];

export function ImageCropper({
  aspectRatios = DEFAULT_ASPECT_RATIOS,
  imageSaving,
  ...props
}: {
  image: File | null;
  open: boolean;
  onClose: () => void;
  onCrop: (image: string, name: string, fileType: string) => Promise<void>;
  aspectRatios?: { width: number; height: number }[];
  imageSaving: boolean;
  onReset: () => void;
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
  const [croppedImage, setCroppedImage] = useState<string | null>(null);
  const [aspectRatio, setAspectRatio] = useState(1);
  const [zoom, setZoom] = useState(1);

  const onCropComplete = useCallback(
    (croppedArea: any, croppedAreaPixels: any) => {
      setCroppedAreaPixels(croppedAreaPixels);

      console.log(croppedArea, croppedAreaPixels);
    },
    []
  );

  const newImage = useMemo(() => {
    if (props.image) {
      return URL.createObjectURL(props.image);
    }
    return null;
  }, [props.image]);

  const showCroppedImage = useCallback(async () => {
    try {
      console.log(props.image?.type);

      const croppedImage = await getCroppedImg(
        newImage,
        croppedAreaPixels,
        props.image?.type || 'image/png'
      );

      console.log('donee', { croppedImage });
      setCroppedImage(croppedImage);
    } catch (e) {
      console.error(e);
    }
  }, [croppedAreaPixels, props.image]);

  return (
    <div className="flex-1">
      {croppedImage && (
        <div className="mb-4 flex items-center">
          <button
            className="mr-2 rounded-md border-2 py-0.5"
            onClick={() => setCroppedImage(null)}
            type="button"
          >
            <ChevronLeft />
          </button>
          Go back
        </div>
      )}
      <div className="flex flex-col">
        {croppedImage ? (
          <img className="rounded-sm" alt="cropped result" src={croppedImage} />
        ) : (
          <>
            <div className="relative h-96">
              {newImage && (
                <Cropper
                  image={newImage}
                  crop={crop}
                  zoom={zoom}
                  aspect={aspectRatio}
                  onCropChange={setCrop}
                  onCropComplete={onCropComplete}
                  onZoomChange={setZoom}
                />
              )}
            </div>
            <div>
              <div className="mt-2 flex justify-around">
                {aspectRatios.length > 1 &&
                  aspectRatios.map((ratio) => {
                    const value = ratio.width / ratio.height;
                    return (
                      <button
                        key={value}
                        className={cn(
                          'rounded-md border-2 border-gray-200 px-5 py-3',
                          aspectRatio === value && 'border-red bg-gray-100'
                        )}
                        onClick={() => setAspectRatio(value)}
                        type="button"
                      >
                        <div
                          className="w-4 rounded-[1px] bg-gray-400"
                          style={{ aspectRatio: value }}
                        />
                      </button>
                    );
                  })}
              </div>
              <div className="mt-2">
                <p>Zoom</p>
                <Slider
                  value={[zoom]}
                  onValueChange={(val) => {
                    setZoom(val[0]);
                  }}
                  min={0}
                  max={4}
                  step={0.01}
                />
              </div>
            </div>
          </>
        )}
        <div className="mt-5 flex w-full items-center justify-between">
          <Button
            className="border bg-white text-red hover:bg-gray-50"
            onClick={() => {
              props.onReset();
            }}
          >
            <Trash2 className="mr-2" size={16} />
            Clear image
          </Button>
          {!croppedImage ? (
            <Button
              className="border bg-black text-white hover:bg-black/75"
              onClick={async () => {
                // save cropped image
                await showCroppedImage();
              }}
            >
              <Eye className="mr-2" size={16} />
              Preview
            </Button>
          ) : (
            <Button
              className="border border-gray-100 bg-green-500 text-white hover:bg-green-600"
              onClick={async () => {
                await props.onCrop(
                  croppedImage,
                  props.image?.name ?? '',
                  props.image?.type ?? ''
                );
              }}
              loading={imageSaving}
            >
              <Save className="mr-2" size={16} />
              Save
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
